import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import ChatBubble from "./ChatBubble";
import OnboardingRightPanel from "./OnboardingRightPanel";
import {
  ChatMessage,
  OnboardingButton,
  OnboardingRightPanelData,
  SubscriptionStepCta,
  OnboardingState,
  onboardingService,
} from "../../services/OnboardingService";
import { httpService } from "../../services/HttpServices";
import { revSyncService } from "../../services/RevSyncService";

interface OnboardingChatProps {
  isOpen: boolean;
  onClose: () => void;
  isClosing?: boolean;
  isOpening?: boolean;
}

const OnboardingChat: React.FC<OnboardingChatProps> = ({
  isOpen,
  onClose,
  isClosing = false,
  isOpening = false,
}) => {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [currentMessage, setCurrentMessage] = useState<ChatMessage | null>(
    null,
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [rightPanelComponent, setRightPanelComponent] = useState<string | null>(
    null,
  );
  const [rightPanelData, setRightPanelData] =
    useState<OnboardingRightPanelData | null>(null);
  const [subscriptionStepCta, setSubscriptionStepCta] =
    useState<SubscriptionStepCta | null>(null);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHandlingAction, setIsHandlingAction] = useState(false);
  const [contractCreationSelections, setContractCreationSelections] = useState<{
    pipelineId: string;
    stageId: string;
  } | null>(null);
  const [renewalSelections, setRenewalSelections] = useState<{
    pipelineId: string;
    stageId: string;
    daysBeforeExpiration: number;
  } | null>(null);
  const [pendingSubscriptionStartDateField, setPendingSubscriptionStartDateField] =
    useState<string | null>(null);
  const [subscriptionSelectedProductId, setSubscriptionSelectedProductId] =
    useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentMessageRef = useRef<ChatMessage | null>(null);
  /** Count of in-flight top-level actions so the loader stays on until all complete (avoids nested/side calls turning it off early) */
  const pendingActionsRef = useRef(0);

  // Always show enable panel on review step, regardless of rightPanelComponent state
  const effectiveRightPanelComponent =
    state?.step === "review" ? "enable_revsync" : rightPanelComponent;

  const syncRightPanel = (message?: ChatMessage | null) => {
    if (message?.metadata?.showInRightPanel) {
      setRightPanelComponent(message.metadata.rightPanelComponent || null);
    } else {
      setRightPanelComponent(null);
      setShowRightPanel(false);
    }
  };

  const filterButtonsForRightPanel = (message: ChatMessage): ChatMessage => {
    let updatedButtons = message.buttons;

    // Remove "Show All Options" button
    updatedButtons = updatedButtons?.filter(
      (button) => button.action !== "show_lifecycle_options",
    );

    // Hide "show_contract_selector" button when contract creation panel auto-opens
    if (
      message.metadata?.rightPanelComponent === "contract_creation_selector"
    ) {
      updatedButtons = updatedButtons?.filter(
        (button) => button.action !== "show_contract_selector",
      );
    }

    // Render all subscription step buttons from backend (no frontend guardrails; backend controls clickable state)

    // Hide "Review Settings" button on review step
    if (message.metadata?.step === "review" || state?.step === "review") {
      updatedButtons = updatedButtons?.filter(
        (button) =>
          !button.label.toLowerCase().includes("review settings") &&
          button.action !== "review_settings",
      );
    }

    // Disable renewal setup button if selections are incomplete
    if (message.metadata?.rightPanelComponent === "renewal_settings_selector") {
      updatedButtons = updatedButtons?.map((button) => {
        // Disable "set up renewals" button if pipeline or stage is missing
        if (
          button.action !== "skip_renewals" &&
          (button.style === "primary" ||
            button.label.toLowerCase().includes("set up") ||
            button.label.toLowerCase().includes("yes"))
        ) {
          // Check if we have complete selections (from state or renewalSelections)
          const hasCompleteSelections =
            (renewalSelections?.pipelineId && renewalSelections?.stageId) ||
            (state?.settings.renewalPipelineId &&
              state?.settings.renewalStageId);
          if (!hasCompleteSelections) {
            return {
              ...button,
              disabled: true,
            } as OnboardingButton & { disabled?: boolean };
          }
        }
        return button;
      });
    }

    return {
      ...message,
      buttons: updatedButtons,
    };
  };

  const fetchOnboardingState = async () => {
    try {
      setIsLoading(true);
      const response = await onboardingService.getOnboardingState();
      const data = response.data;

      setState(data.state);
      setCurrentMessage(filterButtonsForRightPanel(data.currentMessage));
      setMessages(data.messages || []);
      syncRightPanel(data.currentMessage);
      setSubscriptionStepCta(data.subscriptionStepCta ?? null);
      if (data.rightPanel) {
        setRightPanelData(data.rightPanel);
        if (data.rightPanel.show) {
          setRightPanelComponent(data.rightPanel.component);
        }
      } else {
        setRightPanelData(null);
      }
    } catch (error) {
      console.error("Error fetching onboarding state:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLifecycleSelectionChange = (value: string, label: string) => {
    setCurrentMessage((prev) => {
      if (
        !prev ||
        prev.metadata?.rightPanelComponent !== "lifecycle_stage_selector" ||
        !prev.buttons?.length
      ) {
        return prev;
      }

      const updatedButtons = prev.buttons.map((button) => {
        if (button.action !== "set_customer_lifecycle") return button;
        return {
          ...button,
          value,
          label: `Use "${label}"`,
        };
      });

      return {
        ...prev,
        buttons: updatedButtons,
      };
    });
  };

  const handleContractCreationSelectionChange = (
    pipelineId: string,
    pipelineLabel: string,
    stageId: string,
    stageLabel: string,
  ) => {
    // Store selections for saving later
    if (pipelineId && stageId) {
      setContractCreationSelections({ pipelineId, stageId });
    }

    setCurrentMessage((prev) => {
      if (
        !prev ||
        prev.metadata?.rightPanelComponent !== "contract_creation_selector" ||
        !prev.buttons?.length
      ) {
        return prev;
      }

      // Update the save button label when selections are made
      const updatedButtons = prev.buttons.map((button) => {
        // Only update the button that will save the selection
        if (
          (button.action === "accept_contract_suggestion" ||
            button.action === "continue_step") &&
          pipelineId &&
          stageId
        ) {
          return {
            ...button,
            label: `Use "${pipelineLabel}" → "${stageLabel}"`,
          };
        }
        return button;
      });

      return {
        ...prev,
        buttons: updatedButtons,
      };
    });
  };

  const handleRenewalSelectionChange = (
    pipelineId: string,
    pipelineLabel: string,
    stageId: string,
    stageLabel: string,
    daysBeforeExpiration: number,
  ) => {
    // Store selections for saving later (only if both pipeline and stage are selected)
    const isComplete = pipelineId && stageId;
    if (isComplete) {
      setRenewalSelections({ pipelineId, stageId, daysBeforeExpiration });
    } else {
      // Clear selections if incomplete
      setRenewalSelections(null);
    }

    setCurrentMessage((prev) => {
      if (
        !prev ||
        prev.metadata?.rightPanelComponent !== "renewal_settings_selector" ||
        !prev.buttons?.length
      ) {
        return prev;
      }

      // Update the "Yes, Set Up Renewals" button label and disabled state
      const updatedButtons = prev.buttons.map((button) => {
        // Only update buttons that are not "skip_renewals"
        if (button.action !== "skip_renewals") {
          // Check if this looks like a "set up renewals" button (primary style or specific label)
          if (
            button.style === "primary" ||
            button.label.toLowerCase().includes("set up") ||
            button.label.toLowerCase().includes("yes")
          ) {
            return {
              ...button,
              label: `Yes, Set Up Renewals`,
              disabled: !isComplete, // Disable if pipeline or stage is missing
            } as OnboardingButton & { disabled?: boolean };
          }
        }
        return button;
      });

      return {
        ...prev,
        buttons: updatedButtons,
      };
    });
  };

  const handleSubscriptionSelectionChange = (productId: string | null) => {
    setSubscriptionSelectedProductId(productId);
  };

  const handleAction = async (action: string, value?: string) => {
    pendingActionsRef.current += 1;
    setIsHandlingAction(true);
    try {

      // If we're on contract creation step and have selections, save them first
      if (
        rightPanelComponent === "contract_creation_selector" &&
        contractCreationSelections &&
        (action === "accept_contract_suggestion" ||
          action === "continue_step" ||
          action === "show_contract_selector")
      ) {
        // Save pipeline first (nestedCall: don't clear top loader — handleAction will clear it)
        await handleSetValue(
          "netNewDealPipelineId",
          contractCreationSelections.pipelineId,
          { skipUserMessage: true, nestedCall: true },
        );
        // Then save stage
        await handleSetValue(
          "netNewDealStageId",
          contractCreationSelections.stageId,
          { nestedCall: true },
        );
        // Clear selections after saving
        setContractCreationSelections(null);
      }

      // If we're on renewal settings step and have selections, save them first
      // Save for any action except "skip_renewals"
      if (
        rightPanelComponent === "renewal_settings_selector" &&
        renewalSelections &&
        action !== "skip_renewals"
      ) {
        // Save renewal settings sequentially without updating UI state (to prevent flashing)
        // Only the final handleAction response will update state
        try {
          await onboardingService.setValue(
            "renewalPipelineId",
            renewalSelections.pipelineId,
          );
          await onboardingService.setValue(
            "renewalStageId",
            renewalSelections.stageId,
          );
          await onboardingService.setValue(
            "renewalDaysBeforeExpiration",
            renewalSelections.daysBeforeExpiration,
          );
        } catch (error) {
          console.error("Error saving renewal settings:", error);
          // Continue with action even if setValue fails
        }
        // Clear selections after saving
        setRenewalSelections(null);
      }

      // Pass product id on confirm when backend didn't send it (e.g. from right-panel selection)
      const effectiveValue =
        action === "confirm_subscription_product"
          ? value || subscriptionSelectedProductId || undefined
          : value;

      // Subscription step: save product + start date only when user clicks Confirm selection
      if (action === "confirm_subscription_product") {
        if (pendingSubscriptionStartDateField) {
          try {
            await onboardingService.setValue(
              "subscriptionStartDateField",
              pendingSubscriptionStartDateField,
            );
          } catch (err) {
            console.error("Error saving subscription start date:", err);
          }
          setPendingSubscriptionStartDateField(null);
        }
        if (effectiveValue) {
          try {
            await onboardingService.handleAction("set_subscription_product", effectiveValue);
          } catch (err) {
            console.error("Error saving subscription product:", err);
          }
        }
      }

      const response = await onboardingService.handleAction(action, effectiveValue);
      const data = response.data;

      if (data.connectEndpoint) {
        const connectResponse = await httpService.get<any>(
          data.connectEndpoint,
        );
        const authorizationUrl =
          connectResponse?.data?.authorizationUrl ||
          connectResponse?.authorizationUrl;

        if (authorizationUrl) {
          window.location.href = authorizationUrl;
          return;
        }
      }

      setState(data.nextState);
      setCurrentMessage(filterButtonsForRightPanel(data.nextMessage));

      // Replace history with backend's messages array
      if (data.messages) {
        setMessages(data.messages);
      }

      // Add updatedMessages if provided (for user confirmations, etc.)
      const updatedMessages = data.updatedMessages;
      if (updatedMessages && updatedMessages.length > 0) {
        setMessages((prev) => [...prev, ...updatedMessages]);
      }

      syncRightPanel(data.nextMessage);
      setSubscriptionStepCta(data.subscriptionStepCta ?? null);
      if (data.rightPanel) {
        setRightPanelData(data.rightPanel);
        if (data.rightPanel.show) {
          setRightPanelComponent(data.rightPanel.component);
        }
      } else {
        setRightPanelData(null);
      }

      // Close modal immediately when RevSync is enabled
      if (action === "enable_revsync") {
        onClose();
      }
    } catch (error) {
      console.error("Error handling onboarding action:", error);
    } finally {
      pendingActionsRef.current = Math.max(0, pendingActionsRef.current - 1);
      setIsHandlingAction(pendingActionsRef.current > 0);
    }
  };

  const handleSetValue = async (
    field: string,
    value: any,
    options?: { skipUserMessage?: boolean; nestedCall?: boolean },
  ) => {
    if (!options?.nestedCall) {
      pendingActionsRef.current += 1;
      setIsHandlingAction(true);
    }
    try {
      const response = await onboardingService.setValue(field, value);
      const data = response.data;

      setState(data.nextState);
      setCurrentMessage(filterButtonsForRightPanel(data.nextMessage));

      // Replace history with backend's messages array
      if (data.messages) {
        setMessages(data.messages);
      }

      syncRightPanel(data.nextMessage);
    } catch (error) {
      console.error("Error setting onboarding value:", error);
    } finally {
      if (!options?.nestedCall) {
        pendingActionsRef.current = Math.max(0, pendingActionsRef.current - 1);
        setIsHandlingAction(pendingActionsRef.current > 0);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchOnboardingState();
    }
  }, [isOpen]);

  useEffect(() => {
    currentMessageRef.current = currentMessage;
  }, [currentMessage]);

  // Initialize contract creation selections from state
  useEffect(() => {
    if (
      state?.step === "contract_creation" &&
      state.settings.netNewDealPipelineId &&
      state.settings.netNewDealStageId
    ) {
      setContractCreationSelections({
        pipelineId: state.settings.netNewDealPipelineId,
        stageId: state.settings.netNewDealStageId,
      });
    } else if (state?.step !== "contract_creation") {
      // Clear selections when we're not on contract creation step
      setContractCreationSelections(null);
    }
  }, [
    state?.step,
    state?.settings.netNewDealPipelineId,
    state?.settings.netNewDealStageId,
  ]);

  // Initialize renewal selections from state
  useEffect(() => {
    if (
      state?.step === "renewal_settings" &&
      state.settings.renewalPipelineId &&
      state.settings.renewalStageId &&
      state.settings.renewalDaysBeforeExpiration
    ) {
      setRenewalSelections({
        pipelineId: state.settings.renewalPipelineId,
        stageId: state.settings.renewalStageId,
        daysBeforeExpiration: state.settings.renewalDaysBeforeExpiration,
      });
    } else if (state?.step !== "renewal_settings") {
      // Clear selections when we're not on renewal settings step
      setRenewalSelections(null);
    }
  }, [
    state?.step,
    state?.settings.renewalPipelineId,
    state?.settings.renewalStageId,
    state?.settings.renewalDaysBeforeExpiration,
  ]);

  // Sync subscription product selection from state (pre-selected from backend)
  useEffect(() => {
    if (state?.step === "subscription_creation") {
      const fromState =
        state?.subscriptionProductMapping?.defaultProductId ??
        state?.settings?.subscriptionProductMapping?.defaultProductId ??
        null;
      setSubscriptionSelectedProductId((prev) => fromState ?? prev);
    } else {
      setSubscriptionSelectedProductId(null);
    }
  }, [
    state?.step,
    state?.subscriptionProductMapping?.defaultProductId,
    state?.settings?.subscriptionProductMapping?.defaultProductId,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, currentMessage, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("code") || params.get("state")) {
      fetchOnboardingState();
    }
  }, []);

  // Delay right panel appearance until after chat slides in
  useEffect(() => {
    if (effectiveRightPanelComponent && isOpen && !isOpening && !isClosing) {
      // Show immediately for enable panels, otherwise delay slightly
      const delay =
        effectiveRightPanelComponent === "enable_revsync" ||
        effectiveRightPanelComponent === "enable_success"
          ? 0
          : 100;
      const timer = setTimeout(() => {
        setShowRightPanel(true);
      }, delay);
      return () => clearTimeout(timer);
    } else if (!effectiveRightPanelComponent || !isOpen || isClosing) {
      // Hide panel instantly when closing
      setShowRightPanel(false);
    }
  }, [effectiveRightPanelComponent, isOpen, isOpening, isClosing]);

  // Also check current message for enable button when state changes
  useEffect(() => {
    if (currentMessage && state && isOpen) {
      syncRightPanel(currentMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMessage?.id, currentMessage?.buttons, state?.step, isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
        style={{
          opacity: isClosing ? 0 : isOpening ? 0 : 1,
          pointerEvents: isClosing ? "none" : "auto",
          transition: isClosing
            ? "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
            : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
        }}
      />

      <div className="fixed inset-0 z-50 flex flex-col lg:flex-row h-full overflow-hidden">
        <div
          className="w-full lg:max-w-md flex-shrink-0 max-h-[50vh] lg:max-h-full bg-background-default dark:bg-background-dark shadow-2xl flex flex-col overflow-hidden"
          style={{
            transform: isOpening
              ? "translateX(-100%)"
              : isClosing
                ? "translateX(-100%)"
                : "translateX(0)",
            opacity: isClosing ? 0 : 1,
            transition: isClosing
              ? "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
              : "transform 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
          }}
        >
          <div className="flex items-center justify-between px-6 py-3 border-b border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                RevSync Onboarding
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 hover:bg-background-default dark:hover:bg-background-dark rounded transition-colors"
              aria-label="Close onboarding"
            >
              <X className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
            </button>
          </div>
          {(isHandlingAction || isLoading) && (
            <div className="h-0.5 w-full bg-secondary/20 overflow-hidden" role="status" aria-label="Saving">
              <div className="h-full w-2/3 max-w-[200px] bg-secondary rounded-r-full animate-pulse" />
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {isLoading && (
              <div className="text-sm text-text-secondary dark:text-text-secondaryDark">
                Loading onboarding...
              </div>
            )}
            {!isLoading && (
              <>
                {messages.map((message) => (
                  <ChatBubble key={message.id} message={message} />
                ))}
                {currentMessage && (
                  <ChatBubble
                    message={currentMessage}
                    isCurrent
                    onAction={handleAction}
                    isActionDisabled={isHandlingAction}
                    isLoading={isHandlingAction}
                    subscriptionStepCta={subscriptionStepCta}
                  />
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden bg-background-paper dark:bg-background-paperDark lg:bg-transparent">
          {state &&
            (effectiveRightPanelComponent || state.step === "review") && (
              <div
                className="flex-1 min-h-0 overflow-y-auto"
                style={{
                  opacity: isClosing
                    ? 0
                    : effectiveRightPanelComponent === "enable_revsync" ||
                        effectiveRightPanelComponent === "enable_success"
                      ? 1
                      : showRightPanel
                        ? 1
                        : 0,
                  transform:
                    isClosing ||
                    effectiveRightPanelComponent === "enable_revsync" ||
                    effectiveRightPanelComponent === "enable_success"
                      ? "translateX(0)"
                      : showRightPanel
                        ? "translateX(0)"
                        : "translateX(20px)",
                  // Instant hide when closing, smooth show when opening
                  transition: isClosing
                    ? "none"
                    : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1), transform 0.3s cubic-bezier(0, 0, 0.2, 1)",
                }}
              >
                <OnboardingRightPanel
                  component={
                    effectiveRightPanelComponent ||
                    (state.step === "review" ? "enable_revsync" : null)
                  }
                  state={state}
                  rightPanel={rightPanelData}
                  onSetValue={handleSetValue}
                  onClose={() => setRightPanelComponent(null)}
                  onLifecycleSelectionChange={handleLifecycleSelectionChange}
                  onContractCreationSelectionChange={
                    handleContractCreationSelectionChange
                  }
                  onRenewalSelectionChange={handleRenewalSelectionChange}
                  onSubscriptionSelectionChange={handleSubscriptionSelectionChange}
                  onPendingSubscriptionStartDateChange={setPendingSubscriptionStartDateField}
                  onSaveSubscriptionBilling={async (billing) => {
                    try {
                      await revSyncService.updateSettings({
                        subscriptionBilling: {
                          collectionMethod: billing.collectionMethod,
                          daysUntilDue: billing.daysUntilDue,
                          prorationBehavior: billing.prorationBehavior,
                        },
                      });
                    } catch (err) {
                      console.error("Error saving subscription billing:", err);
                    }
                  }}
                  onAction={handleAction}
                />
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default OnboardingChat;
